import * as signalR from "@microsoft/signalr";

export let connection: signalR.HubConnection;
let startPromise: Promise<void> | null = null;
let activeCaseIdGroup: string | null = null;
let messageHandler: ((message: any) => void) | null = null;

let presenceHandler: ((data: any) => void) | null = null;
let typingHandler: ((data: any) => void) | null = null;

// BUG FIX (2026-09-04): every onReceiveX/offReceiveX pair below used to be a single-slot
// `connection.off(event); connection.on(event, handler)` — only ONE handler per event name can
// exist at a time on the shared `connection` singleton. That's fine when only one component ever
// listens for an event, but call-lifecycle events (CallEnded/CallAnswered/CallRejected/etc.) are
// listened to by TWO independent, differently-scoped components: NotificationContext.tsx (mounted
// once, app-wide, shows the ringing popup) and SingleChat/index1.tsx (mounted per chat screen,
// shows in-thread call state). Whichever one registered LAST silently stole the other's slot, and
// when the page-scoped one unmounted and called its own offReceiveX, the event ended up with NO
// handler at all — reproduced as: A cancels a call from mobile before B (on website) answers, and
// B's ringing popup never dismisses because NotificationContext's CallEnded handler had been
// silently evicted by index1.tsx registering/unregistering its own. Fixed by giving these events a
// real multi-subscriber dispatch (one actual SignalR .on() per event, fanning out to however many
// local subscribers are currently registered) — same shape as mobile's DeviceEventEmitter re-emit
// pattern in src/services/signalRService.ts, which was never affected by this bug.
const multiEventSubscribers: Record<string, Set<(...args: any[]) => void>> = {};
let multiWiredEvents = new Set<string>();

const ensureMultiWired = (eventName: string) => {
  if (!connection || multiWiredEvents.has(eventName)) return;
  multiWiredEvents.add(eventName);
  if (!multiEventSubscribers[eventName]) multiEventSubscribers[eventName] = new Set();
  connection.on(eventName, (...args: any[]) => {
    multiEventSubscribers[eventName].forEach((fn) => {
      try {
        fn(...args);
      } catch (e) {
        console.error(`[signalRService] Error in "${eventName}" handler:`, e);
      }
    });
  });
};

const addMultiListener = (eventName: string, handler: (...args: any[]) => void) => {
  if (!connection) return;
  ensureMultiWired(eventName);
  if (!multiEventSubscribers[eventName]) multiEventSubscribers[eventName] = new Set();
  multiEventSubscribers[eventName].add(handler);
};

const removeMultiListener = (eventName: string, handler: (...args: any[]) => void) => {
  multiEventSubscribers[eventName]?.delete(handler);
};

export type ChatConnectionState = "connected" | "reconnecting" | "disconnected";
let connectionState: ChatConnectionState = "disconnected";

const setConnectionState = (state: ChatConnectionState) => {
  connectionState = state;
  window.dispatchEvent(new CustomEvent<ChatConnectionState>("ChatConnectionStateChanged", { detail: state }));
};

export const getConnectionState = (): ChatConnectionState => connectionState;

// Reconnect handler attached once to the HubConnection
const handleReconnected = async () => {
  setConnectionState("connected");
  if (messageHandler && connection) {
    connection.off("ReceiveMessage");
    connection.on("ReceiveMessage", messageHandler);
  }
  if (presenceHandler && connection) {
    connection.off("UserPresenceChanged");
    connection.on("UserPresenceChanged", (data: any) => {
      window.dispatchEvent(new CustomEvent("UserPresenceChanged", { detail: data }));
      if (presenceHandler) presenceHandler(data);
    });
  }
  if (typingHandler && connection) {
    connection.off("UserTyping");
    connection.on("UserTyping", (data: any) => {
      window.dispatchEvent(new CustomEvent("UserTyping", { detail: data }));
      if (typingHandler) typingHandler(data);
    });
  }
  if (activeCaseIdGroup && connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("JoinCaseChat", activeCaseIdGroup);
    } catch (err) {
      console.warn("Error re-joining chat group after reconnect:", err);
    }
  }
};

export const initializeChatHub = (token: string) => {
  if (connection) {
    if (connection.state === signalR.HubConnectionState.Connected) {
      // Connection was already open (e.g. re-entering a chat screen) — these branches used to
      // return without ever updating connectionState, which could leave the "Disconnected" banner
      // stuck forever even though the connection was actually fine.
      setConnectionState("connected");
      return Promise.resolve();
    }
    if (connection.state === signalR.HubConnectionState.Connecting || connection.state === signalR.HubConnectionState.Reconnecting) {
      return startPromise || Promise.resolve();
    }
    if (connection.state === signalR.HubConnectionState.Disconnected) {
      startPromise = connection.start().then(() => {
        startPromise = null;
        setConnectionState("connected");
      }).catch(err => {
        startPromise = null;
        setConnectionState("disconnected");
        throw err;
      });
      return startPromise;
    }
  }

  // A brand-new connection object needs its multi-subscriber dispatchers re-attached — they were
  // wired onto the PREVIOUS connection instance (see multiWiredEvents above), which this replaces.
  multiWiredEvents = new Set();

  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${process.env.REACT_APP_BASE_URL}/chatHub`, {
      transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      accessTokenFactory: () => (localStorage.getItem("token") || "").replace(/(^"|"$)/g, ''),
    })
    // Default withAutomaticReconnect() only retries at [0, 2s, 10s, 30s] and then gives up for good,
    // permanently disconnecting the tab until the user manually refreshes. Retry indefinitely instead,
    // matching the mobile client's backoff, so a longer server blip/deploy self-heals.
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) =>
        Math.min(2000 * Math.pow(1.5, retryContext.previousRetryCount), 30000),
    })
    .configureLogging(signalR.LogLevel.Information)
    .build();

  connection.serverTimeoutInMilliseconds = 60000;
  connection.keepAliveIntervalInMilliseconds = 30000;

  connection.off("UserPresenceChanged");
  connection.on("UserPresenceChanged", (data: any) => {
    window.dispatchEvent(new CustomEvent("UserPresenceChanged", { detail: data }));
    if (presenceHandler) presenceHandler(data);
  });

  connection.off("UserTyping");
  connection.on("UserTyping", (data: any) => {
    window.dispatchEvent(new CustomEvent("UserTyping", { detail: data }));
    if (typingHandler) typingHandler(data);
  });

  connection.onreconnecting(() => setConnectionState("reconnecting"));
  connection.onreconnected(handleReconnected);
  connection.onclose(() => setConnectionState("disconnected"));

  startPromise = connection.start().then(() => {
    startPromise = null;
    setConnectionState("connected");
  }).catch(err => {
    startPromise = null;
    setConnectionState("disconnected");
    throw err;
  });

  return startPromise;
};

// Helper to ensure connection is ready before any invoke
export const ensureConnected = async () => {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    const token = localStorage.getItem("token") || "";
    await initializeChatHub(token);
    return;
  }

  if (connection.state === signalR.HubConnectionState.Connected) {
    return;
  }

  if (startPromise) {
    await startPromise;
  }
};

// Use this to ensure connection is active without re-initializing if unnecessary
export const ensureGlobalConnection = async (token: string) => {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    return await initializeChatHub(token);
  }
};

export const joinChatRoom = async (caseId: string) => {
  try {
    await ensureConnected();
    activeCaseIdGroup = caseId;
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      await connection.invoke("JoinCaseChat", caseId);
    }
  } catch (err) {
    console.warn("SignalR: Failed to join case chat room:", err);
  }
};

export const leaveChatRoom = async (caseId: string) => {
  if (activeCaseIdGroup === caseId) {
    activeCaseIdGroup = null;
  }
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("LeaveCaseChat", caseId);
    } catch (err) {
      console.warn("Failed to leave chat room:", err);
    }
  }
};

export const sendMessage = async (
  caseId: number | null,
  senderUserId: number | null,
  content: string,
  type: string = "text",
  attachmentUrl: string | null = "",
  fileName: string | null = ""
) => {
  await ensureConnected();
  await connection.invoke(
    "SendMessage",
    caseId,
    senderUserId,
    content,
    type,
    attachmentUrl,
    fileName
  );
};

export const onReceiveMessage = (handler: (message: any) => void) => {
  messageHandler = handler;
  if (connection) {
    connection.off("ReceiveMessage");
    connection.on("ReceiveMessage", handler);
  }
};

export const deleteMessage = async (messageId: number, caseId: number) => {
  await ensureConnected();
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    await connection.invoke("DeleteMessage", Number(messageId), Number(caseId));
  }
};

export const onMessageDeleted = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("MessageDeleted");
    connection.on("MessageDeleted", (msgId: any, cId: any) => {
      handler({ messageId: Number(msgId), caseId: Number(cId) });
    });
  }
};

export const offMessageDeleted = () => {
  if (connection) {
    connection.off("MessageDeleted");
  }
};

export const disconnectChatHub = async () => {
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.stop();
    } catch (err) {
      console.warn("Error while disconnecting SignalR:", err);
    }
  }
};

export const sendSOSAlert = async (caseId: any, senderId: any) => {
  const nCaseId = Number(caseId);
  const nUserId = Number(senderId);

  if (isNaN(nCaseId) || isNaN(nUserId)) {
    console.error("SOS Failed: Invalid IDs", { caseId, senderId });
    return;
  }

  try {
    await ensureConnected();
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      await connection.invoke("TriggerSOS", nCaseId, nUserId);
    }
  } catch (err) {
    console.error("SignalR TriggerSOS Error:", err);
    throw err;
  }
};

export const onReceiveSOS = (callback: (data: any) => void) => {
  if (connection) {
    connection.off("AdminNotified");
    connection.on("AdminNotified", callback);
  }
};

// Global listener for new messages (for notifications)
export const onReceiveGlobalMessage = (handler: (message: any) => void) => {
  if (!connection) return;
  connection.off("ReceiveMessageGlobal");
  connection.on("ReceiveMessageGlobal", handler);
};

export const onReceiveGlobalSOS = (handler: (data: any) => void) => {
  if (!connection) return;
  connection.off("ReceiveGlobalAdminAlert");
  connection.on("ReceiveGlobalAdminAlert", handler);
};

export const offReceiveGlobalSOS = () => {
  if (connection) {
    connection.off("ReceiveGlobalAdminAlert");
  }
};

export const onReceiveNotification = (handler: (data: any) => void) => {
  if (!connection) return;
  connection.off("ReceiveNotification");
  connection.on("ReceiveNotification", handler);
};

export const offReceiveNotification = () => {
  if (connection) {
    connection.off("ReceiveNotification");
  }
};

// --- Read Receipts ---
export const markAsRead = async (caseId: number, userId: number) => {
  await ensureConnected();
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("MarkAsRead", caseId, userId);
    } catch (err) {
      console.error("Failed to invoke MarkAsRead", err);
    }
  }
};

export const onReceiveReadReceipt = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("MessagesRead");
    connection.on("MessagesRead", handler);
  }
};

export const offReceiveReadReceipt = () => {
  if (connection) {
    connection.off("MessagesRead");
  }
};

// --- Chat List Global Listeners (for unread badge counting outside the active chat room) ---

/**
 * Register a listener to receive ALL incoming messages (across all joined rooms).
 * Used by the chat list to increment unread counts for cases not currently open.
 * Pass `null` to unregister.
 */
export const onGlobalReceiveMessage = (handler: ((message: any) => void) | null) => {
  if (!connection) return;
  connection.off("ReceiveMessage_global_list");
  if (handler) {
    // We piggyback on the existing "ReceiveMessage" event; we register a separate named copy
    // using a closure so the SingleChat handler is not displaced
    connection.off("ReceiveMessage_global_list");
    // Use the raw connection to add a second listener for the same event
    (connection as any)._handlers = (connection as any)._handlers || {};
    // Register via on() with a unique tracking key via off+on pattern
    // Since SignalR allows multiple handlers, we just call on() again
    connection.on("ReceiveMessage", handler);
  }
};

export const offGlobalReceiveMessage = (handler: ((message: any) => void)) => {
  if (connection && handler) {
    connection.off("ReceiveMessage", handler);
  }
};

/**
 * Register a listener for MessagesRead events from the server.
 * Used by the chat list to clear unread counts when another user (or same user in another tab) opens a chat.
 * Pass `null` to unregister.
 */
export const onGlobalMessagesRead = (handler: ((data: any) => void) | null) => {
  if (!connection) return;
  if (handler) {
    connection.on("MessagesRead", handler);
  }
};

export const offGlobalMessagesRead = (handler: ((data: any) => void)) => {
  if (connection && handler) {
    connection.off("MessagesRead", handler);
  }
};

// NOTE: all of the call-lifecycle events below (IncomingCall, CallEnded, PstnFallbackAttempted,
// CallAnswered, CallRejected, CallParticipantsUpdated) use the multi-subscriber helpers, not raw
// connection.off/on — see the multiEventSubscribers comment above. The offReceiveX functions now
// take the SAME handler reference that was passed to onReceiveX, so unmounting ONE subscriber
// (e.g. index1.tsx leaving a case's chat) never removes another still-mounted subscriber's
// listener for the same event (e.g. NotificationContext.tsx's app-wide ringing popup).
export const onReceiveCallInvite = (handler: (data: any) => void) => {
  addMultiListener("IncomingCall", handler);
};

export const offReceiveCallInvite = (handler: (data: any) => void) => {
  removeMultiListener("IncomingCall", handler);
};

export const onReceiveCallEnded = (handler: (data: any) => void) => {
  addMultiListener("CallEnded", handler);
};

export const offReceiveCallEnded = (handler: (data: any) => void) => {
  removeMultiListener("CallEnded", handler);
};

// PSTN fallback (BonVoice): backend broadcasts this once a server-side fallback dial attempt
// resolves (fired only for a v1-eligible call whose callee never joined Agora in time) so the
// chat bubble can update from "Calling..." straight to its final PSTN-aware text without a
// refetch. `data.success` reflects only whether the phone was DIALED, never whether it was
// answered — see PstnFallbackHostedService on the backend.
export const onReceivePstnFallbackAttempted = (handler: (data: any) => void) => {
  addMultiListener("PstnFallbackAttempted", handler);
};

export const offReceivePstnFallbackAttempted = (handler: (data: any) => void) => {
  removeMultiListener("PstnFallbackAttempted", handler);
};

// Backend broadcasts this to Clients.User(id) — every one of THIS user's own live
// connections/tabs/devices — the moment one of them successfully joins the call. Used to stop
// a still-ringing tab/device on this same account (e.g. this web tab was ringing while the
// user answered on mobile) instead of it ringing forever with no signal that the call was
// already picked up elsewhere.
export const onReceiveCallAnswered = (handler: (data: any) => void) => {
  addMultiListener("CallAnswered", handler);
};

export const offReceiveCallAnswered = (handler: (data: any) => void) => {
  removeMultiListener("CallAnswered", handler);
};

export const onReceiveCallRejected = (handler: (data: any) => void) => {
  addMultiListener("CallRejected", handler);
};

export const offReceiveCallRejected = (handler: (data: any) => void) => {
  removeMultiListener("CallRejected", handler);
};


export const onCallParticipantsUpdated = (handler: (data: any) => void) => {
  addMultiListener("CallParticipantsUpdated", handler);
};

export const offCallParticipantsUpdated = (handler: (data: any) => void) => {
  removeMultiListener("CallParticipantsUpdated", handler);
};

// SESSION MANAGEMENT (2026-09): backend broadcasts this to Clients.User(userId) — ALL of this
// account's live connections, both web and mobile — the instant a new login on the SAME platform
// replaces this tab's session row (AuthController.IssueSessionAsync). Consumers must compare the
// payload's EvictedSessionId against their OWN current "sid" (see Utils/jwt.ts) and only react on
// a match, since the broadcast also reaches the surviving platform's connection.
export const onReceiveSessionEvicted = (handler: (data: { EvictedSessionId?: string; Platform?: string }) => void) => {
  addMultiListener("SessionEvicted", handler);
};

export const offReceiveSessionEvicted = (handler: (data: any) => void) => {
  removeMultiListener("SessionEvicted", handler);
};

export const onUserPresenceChanged = (handler: (data: any) => void) => {
  presenceHandler = handler;
  if (connection) {
    connection.off("UserPresenceChanged");
    connection.on("UserPresenceChanged", handler);
  }
};

export const offUserPresenceChanged = () => {
  presenceHandler = null;
  if (connection) {
    connection.off("UserPresenceChanged");
  }
};

export const onUserTyping = (handler: (data: any) => void) => {
  typingHandler = handler;
  if (connection) {
    connection.off("UserTyping");
    connection.on("UserTyping", handler);
  }
};

export const offUserTyping = () => {
  typingHandler = null;
  if (connection) {
    connection.off("UserTyping");
  }
};

export const sendTypingStatus = async (caseId: number | string, isTyping: boolean) => {
  await ensureConnected();
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("SendTypingStatus", Number(caseId), isTyping);
    } catch (err) {
      console.warn("SignalR SendTypingStatus error:", err);
    }
  }
};

